<template>
  <div>
    <CollapsibleSection v-if="isLoading" :header-label="$t(title)" body-class="flex flex-wrap">
      <div class="w-full md:w-1/2 flex flex-col md:flex-row items-center gap-x-1 p-2">
        <span class="bg-brand-gray-250 md:w-1/3 h-4"></span>
        <span class="bg-brand-gray-250 md:w-2/3 h-4"></span>
      </div>
      <div class="w-full md:w-1/2 flex flex-col md:flex-row items-center gap-x-1 p-2">
        <span class="bg-brand-gray-250 md:w-1/3 h-4"></span>
        <span class="bg-brand-gray-250 md:w-2/3 h-4"></span>
      </div>
      <div class="w-full md:w-1/2 flex flex-col md:flex-row items-center gap-x-1 p-2">
        <span class="bg-brand-gray-250 md:w-1/3 h-4"></span>
        <span class="bg-brand-gray-250 md:w-2/3 h-4"></span>
      </div>
      <div class="w-full md:w-1/2 flex flex-col md:flex-row items-center gap-x-1 p-2">
        <span class="bg-brand-gray-250 md:w-1/3 h-4"></span>
        <span class="bg-brand-gray-250 md:w-2/3 h-4"></span>
      </div>
      <div class="w-full md:w-1/2 flex flex-col md:flex-row items-center gap-x-1 p-2">
        <span class="bg-brand-gray-250 md:w-1/3 h-4"></span>
        <span class="bg-brand-gray-250 md:w-2/3 h-4"></span>
      </div>
      <div class="w-full md:w-1/2 flex flex-col md:flex-row items-center gap-x-1 p-2">
        <span class="bg-brand-gray-250 md:w-1/3 h-4"></span>
        <span class="bg-brand-gray-250 md:w-2/3 h-4"></span>
      </div>
    </CollapsibleSection>
    <template v-else>
      <div class="flex items-center justify-between border-b border-brand-gray-350 w-full mb-4">
        <div class="pb-2 w-full">
          <h2 class="text-xl text-gray-700 font-normal mx-3">
            {{ $t(props.panelTitle) }}
          </h2>
        </div>
      </div>
      <CollapsibleSection :header-label="$t(title)" body-class="flex flex-wrap">
        <div
          v-if="showFlags.showEnglishName"
          class="w-full md:w-1/2 flex flex-col md:flex-row items-start gap-x-1 p-0.5"
        >
          <span class="md:w-1/3 pt-0.5 ltr:font-sans font-bold text-brand-black-body text-xs">
            {{ $t(labels.englishName) }}:
          </span>
          <span class="md:w-2/3">
            {{ establishmentInfo.englishName }}
          </span>
        </div>
        <div
          v-if="showFlags.showArabicName"
          class="w-full md:w-1/2 flex flex-col md:flex-row items-start gap-x-1 p-0.5"
        >
          <span class="md:w-1/3 pt-0.5 ltr:font-sans font-bold text-brand-black-body text-xs">
            {{ $t(labels.arabicName) }}:
          </span>
          <span class="md:w-2/3">
            {{ establishmentInfo.arabicName }}
          </span>
        </div>
        <div
          v-if="showFlags.showDepartment"
          class="w-full md:w-1/2 flex flex-col md:flex-row items-start gap-x-1 p-0.5"
        >
          <span class="md:w-1/3 pt-0.5 ltr:font-sans font-bold text-brand-black-body text-xs">
            {{ $t(labels.department) }}:
          </span>
          <span class="md:w-2/3">
            {{ establishmentInfo.department.text }}
          </span>
        </div>
        <div
          v-if="showFlags.showSponsorType"
          class="w-full md:w-1/2 flex flex-col md:flex-row items-start gap-x-1 p-0.5"
        >
          <span class="md:w-1/3 pt-0.5 ltr:font-sans font-bold text-brand-black-body text-xs">
            {{ $t(labels.sponsorType) }}:
          </span>
          <span class="md:w-2/3">
            <slot name="sponsorType" :profile="establishmentInfo">
              {{ establishmentInfo.sponsorType.text }}
            </slot>
          </span>
        </div>
        <div
          v-if="showFlags.showLicenseIssueDate"
          class="w-full md:w-1/2 flex flex-col md:flex-row items-start gap-x-1 p-0.5"
        >
          <span class="md:w-1/3 pt-0.5 ltr:font-sans font-bold text-brand-black-body text-xs">
            {{ $t(labels.licenseIssueDate) }}:
          </span>
          <span class="md:w-2/3">
            {{ establishmentInfo.licenseIssueDatePretty }}
          </span>
        </div>
        <div
          v-if="showFlags.showNumber"
          class="w-full md:w-1/2 flex flex-col md:flex-row items-start gap-x-1 p-0.5"
        >
          <span class="md:w-1/3 pt-0.5 ltr:font-sans font-bold text-brand-black-body text-xs">
            {{ $t(labels.number) }}:
          </span>
          <span class="md:w-2/3">
            {{ establishmentInfo.number }}
          </span>
        </div>
        <slot name="moreRows" :profile="establishmentInfo"></slot>
      </CollapsibleSection>
    </template>
  </div>
</template>

<script setup>
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import axiosInstance from '@/services'
import { DateTime } from 'luxon'
import { onMounted, ref } from 'vue'

const isLoading = ref(true)
const establishmentInfo = ref()

const props = defineProps({
  title: {
    type: String,
    default: () => 'GuarantorData'
  },
  showFlags: {
    type: Object,
    default: () => ({
      showEnglishName: true,
      showArabicName: true,
      showDepartment: true,
      showSponsorType: true,
      showLicenseIssueDate: true,
      showNumber: true
    })
  },
  labels: {
    type: Object,
    default: () => ({
      englishName: 'GuarantorEn',
      arabicName: 'GuarantorAr',
      department: 'GuarantorDepartment',
      sponsorType: 'GuarantorType',
      licenseIssueDate: 'licenceIssueDate',
      number: 'GuarantorNumber'
    })
  },
  panelTitle: {
    type: String,
    default: () => ''
  }
  
})

const formatDate = (date) => DateTime.fromISO(date).toFormat('dd/MM/yyyy')

onMounted(async () => {
  establishmentInfo.value = await axiosInstance.get('/api/establishment/info')
  const { licenseIssueDate, licenseExpiryDate, subscriptionStartDate, subscriptionEndDate } =
    establishmentInfo.value

  establishmentInfo.value = {
    ...establishmentInfo.value,
    licenseIssueDatePretty: formatDate(licenseIssueDate),
    licenseExpiryDatePretty: formatDate(licenseExpiryDate),
    subscriptionStartDatePretty: formatDate(subscriptionStartDate),
    subscriptionEndDatePretty: formatDate(subscriptionEndDate)
  }

  isLoading.value = false
})
</script>

<style lang="scss" scoped></style>
